import { joiResolver } from '@hookform/resolvers/joi';
import { Radio } from '@mui/material';


import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { TFunction, useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Icon,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  stringToPayloadFormat,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  TableState,
  TextField,
  Typography,
  useDataTable,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useParams } from 'react-router-dom';
import { MutationStatus } from 'react-query';
import { candidateService } from '../../../api';
import ControlledAutocomplete from '../../../components/form-items/ControlledAutocomplete';
import AddSubjectModalSchema from './schema/AddSubjectModalSchema';
import {
  TypeAndReasonModel,
  TypesAndReasonsResponse,
} from '../../../api/candidate/special-consideration/model';
import { usePostAddSubject } from '../../../api-refactored/special-consideration/usePostSubject';
import { stringToSemanticDisplay } from '../../../utils/DateUtils';
import {
  SubjectModel,
  useGetSubjectList,
} from '../../../api-refactored/special-consideration/useGetSubjectList';
import { PaginatedData } from '../../../models/List';
import { useRemainingCharacter } from '../../../hooks/useRemainingCharacter';

const useStyles = makeStyles()((theme) =>
({
  radioButton: {
    padding: '0px',
    margin: '0px',
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  buttonContainer: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  addIcon: {
    verticalAlign: 'sub',
    marginRight: 10,
    color: theme.color.icon.iconInverse,
  },
  selectSubjectText: {
    marginBottom: theme.spacing(3),
  },
  reasonTitle: {
    marginBottom: theme.spacing(3),
  },
  customSelect: {
    marginTop: theme.spacing(3),
  },
  divider: {
    marginTop: theme.spacing(5),
  },
  textDisabled: {
    color: theme.color.text.textDisabled,
  },
}));

interface DialogButtonsProps {
  onDialogClose: () => void;
  onAddClick: () => void;
  apiButtonDisabled: boolean;
  apiButtonStatus: MutationStatus;
  idButtonSubmit?: string;
  idButtonCancel?: string;
}

const DialogButtons = ({
  apiButtonDisabled,
  onAddClick,
  onDialogClose,
  apiButtonStatus,
  idButtonSubmit,
  idButtonCancel,
}: DialogButtonsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <DialogActions id="candidate-dialog-action">
      <Box className={classes.buttonContainer}>
        <Button id={idButtonCancel} onClick={onDialogClose} variant="text">
          {t('specialConsideration->>Dialog.Cancel')}
        </Button>
        <ApiButton
          status={apiButtonStatus}
          type="button"
          onClick={onAddClick}
          disabled={apiButtonDisabled}
          id={idButtonSubmit}
        >
          <Icon size="M" className={classes.addIcon} name={IconName.add} />
          {t('specialConsideration->>Dialog.Add')}
        </ApiButton>
      </Box>
    </DialogActions>
  );
};

interface SubjectSelectedProps {
  subjectUuid: string;
  subject: string;
  paperUuid: string;
  examDate: string;
}

const TYPE_PLACEHOLDER = 'Select Special Consideration Type';
const REASON_PLACEHOLDER = 'Select Reason';

const getTableColumns = (t: TFunction<'translation'>, state: TableState, classes: Record<string,string>) => {
  const tableColumns: MUIDataTableColumnDef[] = [
    {
      label: t('specialConsideration->>ApplyForSpecialConsideration.ExamLevel'),
      name: 'examLevel',
      options: {
        setCellProps: () => ({ style: { width: '18%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography
            variant="body2"
            className={isRowDisabled(dataIndex, state) ? classes.textDisabled : ''}
          >
            {state.data[dataIndex].examLevel}
          </Typography>
        ),
      },
    },
    {
      label: t('specialConsideration->>ApplyForSpecialConsideration.Exam Series'),
      name: 'examSeries',
      options: {
        customBodyRenderLite: (dataIndex: number) => (
          <Typography
            variant="body2"
            className={isRowDisabled(dataIndex, state) ? classes.textDisabled : ''}
          >
            {state.data[dataIndex].examSeries}
          </Typography>
        ),
      },
    },
    {
      label: t('specialConsideration->>ApplyForSpecialConsideration.SubjectPaper'),
      name: 'subject',
      options: {
        customBodyRenderLite: (dataIndex: number) => (
          <Typography
            variant="body2"
            className={isRowDisabled(dataIndex, state) ? classes.textDisabled : ''}
          >
            {state.data[dataIndex].subject}
          </Typography>
        ),
      },
    },
    {
      label: t('specialConsideration->>ApplyForSpecialConsideration.ModeOfAssessment'),
      name: 'modeOfAssessment',
      options: {
        customBodyRenderLite: (dataIndex: number) => (
          <Typography
            variant="body2"
            className={isRowDisabled(dataIndex, state) ? classes.textDisabled : ''}
          >
            {state.data[dataIndex].modeOfAssessment}
          </Typography>
        ),
      },
    },
    {
      label: t('specialConsideration->>ApplyForSpecialConsideration.Exam Date'),
      name: 'examDate',
      options: {
        setCellProps: () => ({ style: { width: '18%' } }),
        customBodyRenderLite: (dataIndex: number) => {
          let displayDate = '-';
          if (state.data[dataIndex] && state.data[dataIndex].examDate) {
            displayDate = stringToSemanticDisplay(state.data[dataIndex].examDate);
          }
          return (
            <Typography
              variant="body2"
              className={isRowDisabled(dataIndex, state) ? classes.textDisabled : ''}
            >
              {displayDate}
            </Typography>
          );
        },
      },
    },
  ];
  return tableColumns;
};

const CustomCheckbox = (props: {}) => {
  const newProps = { ...props };
  const { classes } = useStyles();
  return <Radio className={classes.radioButton} size="medium" {...newProps} />;
};

const SubjectInfoHeader = ({ subject }: { subject: string }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <>
      <div className={classes.divider}>
        <Divider />
      </div>
      <Typography variant="h3" className={classes.selectSubjectText}>
        {subject || ''}
      </Typography>
      <Typography variant="h4" className={classes.reasonTitle}>
        {t('specialConsideration->>Dialog.SelectScTypeAndReason')}
      </Typography>
    </>
  );
};

const isRowDisabled = (dataIndex: number, state: TableState) => {
  if (dataIndex === undefined) {
    return true;
  }
  return state.data[dataIndex].selected;
};

const DialogTitleContainer = () => {
  const { t } = useTranslation();
  return (
    <DialogTitle>
      <Typography variant="h4">
        {t('specialConsideration->>ApplyForSpecialConsideration.AddSubjectPaper')}
      </Typography>
    </DialogTitle>
  );
};

interface TypeAndReasonControlProps {
  typesAndReasonsData: TypesAndReasonsResponse;
  setTypeValue: (value: string) => void;
  setReasonValue: (value: string) => void;
  typeValue: string;
  formHook: UseFormReturn;
}

const TypeAndReasonControl = ({
  typesAndReasonsData,
  setReasonValue,
  setTypeValue,
  typeValue,
  formHook,
}: TypeAndReasonControlProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { control } = formHook;

  return (
    <>
      {typesAndReasonsData?.specialConsiderationTypes && (
        <ControlledAutocomplete<TypeAndReasonModel, false, false, undefined>
          label={t(
            'specialConsideration->>ApplyForSpecialConsideration.Special Consideration Type'
          )}
          control={control}
          name="specialConsiderationType"
          id="input-select-special-consideration-type"
          error={false}
          options={typesAndReasonsData?.specialConsiderationTypes || []}
          getOptionLabel={(option: TypeAndReasonModel) => option.children}
          placeholder={TYPE_PLACEHOLDER}
          isOptionEqualToValue={(o, v) => o.key === v.key}
          transform={{
            input: (selectedOption, options) =>
              options.find((option) => option.value === selectedOption) ?? undefined,
            output: (selectedOption: TypeAndReasonModel) => {
              const value = selectedOption?.value ?? undefined;
              setTypeValue(value);
              return value;
            },
          }}
          isRequired
        />
      )}
      <div className={classes.customSelect}>
        {typesAndReasonsData?.specialConsiderationTypeReasonMap &&
          typesAndReasonsData.specialConsiderationTypeReasonMap[typeValue] && (
            <ControlledAutocomplete<TypeAndReasonModel, false, false, undefined>
              label={t('specialConsideration->>ApplyForSpecialConsideration.Reason')}
              control={control}
              name="specialConsiderationReason"
              id="input-select-special-consideration-reason"
              error={false}
              options={
                typesAndReasonsData?.specialConsiderationTypeReasonMap &&
                  typesAndReasonsData.specialConsiderationTypeReasonMap[typeValue]
                  ? typesAndReasonsData.specialConsiderationTypeReasonMap[typeValue]
                  : []
              }
              getOptionLabel={(option: TypeAndReasonModel) => option.children}
              placeholder={REASON_PLACEHOLDER}
              transform={{
                input: (selectedOption, options) =>
                  options.find((option) => option.value === selectedOption) ?? undefined,
                output: (selectedOption: TypeAndReasonModel) => {
                  const value = selectedOption?.value ?? undefined;
                  setReasonValue(value);
                  return value;
                },
              }}
              isRequired
            />
          )}
      </div>
    </>
  );
};

interface AddSubjectModalProps {
  modalState: boolean;
  onDialogClose: () => void;
  refetchSubjectTable: () => void;
}

const AddSubjectModal = ({
  modalState,
  onDialogClose,
  refetchSubjectTable,
}: AddSubjectModalProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const formHook = useForm({
    mode: 'onSubmit',
    resolver: joiResolver(AddSubjectModalSchema()),
  });
  const { uuid: requestUuid } = useParams<{ uuid: string }>();
  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
  });
  const { mutate: mutateGetSubjectListing, isLoading } = useGetSubjectList({
    onSuccess: (data: PaginatedData<SubjectModel>) => {
      if (data) {
        tableDispatch({
          type: 'UPDATE_DATA',
          payload: {
            data: data.content,
            count: data.totalElements,
          },
        } as TableActionTypes);
      }
    },
  });
  const {
    data: typesAndReasonsData,
  } = candidateService.specialConsideration.candidate.useGetTypesAndReasons();
  const addSubjectRequest = usePostAddSubject({
    onSuccess: () => {
      refetchSubjectTable();
      onDialogClose();
    },
  });

  const [typeValue, setTypeValue] = useState<string>('');
  const [reasonValue, setReasonValue] = useState<string>('');
  const [explanationValue, setExplanationValue] = useState<string>('');
  const [subjectSelected, setSubjectSelected] = useState<SubjectSelectedProps>({
    examDate: '',
    paperUuid: '',
    subject: '',
    subjectUuid: '',
  });
  const [selectedSubjectId, setSelectedSubjectId] = useState<number[]>([]);

  useEffect(() => {
    mutateGetSubjectListing({
      uuid: requestUuid,
      page: state.page,
      size: state.size,
    });
  }, [mutateGetSubjectListing, requestUuid, state.page, state.size]);

  const tableColumns = getTableColumns(t, state, classes);
  const customOptions: MUIDataTableOptions = {
    ...tOptions,
    selectableRows: 'single',
    selectToolbarPlacement: 'none',
    rowsSelected: selectedSubjectId,
    isRowSelectable: (dataIndex: number) => !isRowDisabled(dataIndex, state),
    onRowSelectionChange: (currentRowsSelected: any, allRowsSelected: any, rowsSelected: any) => {
      if (tOptions.onRowSelectionChange) {
        tOptions.onRowSelectionChange(currentRowsSelected, allRowsSelected, rowsSelected);
      }
      setSelectedSubjectId(rowsSelected || []);
      if (rowsSelected) {
        const indexSelected = rowsSelected[0];
        const rowsSelectedData = state.data[indexSelected];
        setSubjectSelected({
          subjectUuid: rowsSelectedData.subjectUuid,
          paperUuid: rowsSelectedData.paperUuid,
          subject: rowsSelectedData.subject,
          examDate: rowsSelectedData.examDate,
        });
      }
    },
    pagination: true,
  };

  const onAddClick = () => {
    addSubjectRequest.mutate({
      examDate: stringToPayloadFormat(subjectSelected.examDate),
      explanation: explanationValue,
      paperUuid: subjectSelected.paperUuid,
      reason: reasonValue,
      requestUuid,
      specialConsiderationType: typeValue,
      subjectUuid: subjectSelected.subjectUuid,
    });
  };

  const maximumRemarksLength = 500;
  const { remainingCharMsg, charactersRemainingHandler } = useRemainingCharacter({
    maximumCharLength: maximumRemarksLength,
  });

  return (
    <Dialog open={modalState} maxWidth="md" onClose={onDialogClose}>
      <DialogTitleContainer />
      <DialogContent>
        <DialogContentText>
          {t('specialConsideration->>Dialog.SelectSubjectPaperInstruction')}
        </DialogContentText>
        <MUIDataTable
          isLoading={isLoading}
          columns={tableColumns}
          data={state.data}
          state={state}
          dispatch={tableDispatch}
          options={customOptions}
          components={{
            Checkbox: CustomCheckbox,
          }}
        />
        {subjectSelected.subjectUuid && (
          <>
            <SubjectInfoHeader subject={subjectSelected.subject} />
            {typesAndReasonsData && (
              <TypeAndReasonControl
                setReasonValue={setReasonValue}
                setTypeValue={setTypeValue}
                typeValue={typeValue}
                typesAndReasonsData={typesAndReasonsData}
                formHook={formHook}
              />
            )}
            <div className={classes.customSelect}>
              <TextField
                value={explanationValue}
                isRequired
                label={t('specialConsideration->>ApplyForSpecialConsideration.Remarks')}
                subLabel={t('specialConsideration->>ApplyForSpecialConsideration.RemarksSubLabel')}
                placeholder={t('specialConsideration->>ApplyForSpecialConsideration.EnterRemarks')}
                name="Explanation"
                type="text"
                fullWidth
                multiline
                rows={4}
                helperText={remainingCharMsg}
                inputProps={{
                  maxLength: maximumRemarksLength,
                }}
                onChange={(e) => {
                  setExplanationValue(e.target.value);
                  charactersRemainingHandler(e.target.value.length);
                }}
              />
            </div>
          </>
        )}
      </DialogContent>
      <DialogButtons
        idButtonSubmit="button-add-subject-paper"
        idButtonCancel="button-cacel-subject-paper"
        apiButtonDisabled={!explanationValue.trim() || !typeValue || !reasonValue}
        apiButtonStatus={addSubjectRequest.status}
        onAddClick={onAddClick}
        onDialogClose={onDialogClose}
      />
    </Dialog>
  );
};

export default AddSubjectModal;
